<script setup>
/**
 * ItemDialogActions Component
 * Premium add-to-cart action area for ItemDialog
 * Handles qty stepper, price display, and validation
 * Supports CUMULATIVE promo pricing and step hints
 *
 * OnlyBox rules:
 * - onlyBox=1 items can only be ordered in multiples of boxSize
 * - +/- buttons increment/decrement by boxSize
 * - Typed qty must be 0 or multiple of boxSize
 */
import { ref, watch, toRef } from 'vue'
import { useCartStore } from '../../../../../app/store/cart.js'
import { useItemPricing } from '../../../../../core/composables/useItemPricing.js'
import ItemDialogQtyStepper from './ItemDialogQtyStepper.vue'
import ItemDialogPriceErrorModal from './ItemDialogPriceErrorModal.vue'

const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
})

const cartStore = useCartStore()

// Shared effective-pricing logic — single source of truth across the catalog UI
// (item dialog + immersive catalog). See useItemPricing for the full breakdown.
const {
  isOutOfStock,
  hasValidPrice,
  cartQty,
  isInCart,
  isOnlyBox,
  boxSize,
  hasValidBoxSize,
  stepSize,
  hasPromo,
  hasDiscount,
  discountDisplayInfo,
  nextStepHintWithBoxes,
  maxCapInfo,
  promoLadder,
  formattedPrice,
  originalPrice,
  hasPikadon,
  pikadonValue,
  lineTotal,
  lineTotalWithVat,
} = useItemPricing(toRef(props, 'item'))

// Price error modal state
const showPriceError = ref(false)

// Toast state
const showToast = ref(false)
const toastMessage = ref('')

// Show a one-time "added to cart" toast on the first add
watch(cartQty, (newQty, oldQty) => {
  if (oldQty === 0 && newQty > 0) {
    showAddedToast()
  }
})

// Show toast notification
function showAddedToast() {
  toastMessage.value = 'נוסף לעגלה'
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 2000)
}

// Handle "+ מארז" button click
function handleAddBox() {
  if (!props.item?.makat) return

  // Check price first
  if (!hasValidPrice.value) {
    showPriceError.value = true
    return
  }

  // Add boxSize units
  const currentQty = cartQty.value
  const newQty = currentQty + boxSize.value

  if (currentQty === 0) {
    // First add
    const result = cartStore.addItem(props.item.makat, boxSize.value)
    if (!result.ok && result.message === 'PRICE_NOT_FOUND') {
      showPriceError.value = true
    }
  } else {
    // Update existing
    const result = cartStore.setQty(props.item.makat, newQty)
    if (!result.ok && result.message === 'PRICE_NOT_FOUND') {
      showPriceError.value = true
    }
  }
}

// Handle qty update from stepper
function handleQtyUpdate(newQty) {
  if (!props.item?.makat) return

  // Check price
  if (!hasValidPrice.value) {
    showPriceError.value = true
    return
  }

  // Use addItem for first add, setQty for updates
  let result
  if (cartQty.value === 0) {
    result = cartStore.addItem(props.item.makat, newQty)
  } else {
    result = cartStore.setQty(props.item.makat, newQty)
  }

  // Handle store rejection (box-multiple violations are covered by the
  // always-visible box message + the stepper's shake feedback)
  if (!result.ok && result.message === 'PRICE_NOT_FOUND') {
    showPriceError.value = true
  }
}

// Handle remove from stepper
function handleRemove() {
  if (!props.item?.makat) return
  cartStore.removeItem(props.item.makat)
}

// Handle trash button - clear all qty
function handleTrash() {
  if (!props.item?.makat) return
  cartStore.removeItem(props.item.makat)
}
</script>

<template>
  <div class="dialog-actions">
    <!-- Out of Stock Banner -->
    <div v-if="isOutOfStock" class="out-of-stock-banner">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </svg>
      <span>חסר זמנית במלאי</span>
    </div>

    <template v-else>
      <!-- Toast Notification -->
      <Transition name="toast">
        <div v-if="showToast" class="toast">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ toastMessage }}</span>
        </div>
      </Transition>

      <!-- Price Display (idle = nothing in cart yet — on mobile the row is hidden
           then, since the unit price is already shown in the details price box) -->
      <div
        v-tutorial="{
          id: 'item-dialog-price',
          text: 'כאן מוצג המחיר ליחידה — ואחרי ההוספה לעגלה גם סה״כ הפריט, כולל מע״מ ופיקדון',
          importance: 'info',
          showOnce: true,
          autoTrigger: false,
          group: 'item-dialog-intro',
          step: 1,
          intro: false,
        }"
        class="price-display"
        :class="{ idle: !lineTotal }"
      >
        <div class="price-col">
          <span class="price-label">מחיר ליחידה</span>
          <span
            class="price-value"
            :class="{
              'no-price': !hasValidPrice,
              'promo-price': hasPromo,
              'discount-price': hasDiscount && !hasPromo,
            }"
          >
            <template v-if="hasValidPrice">
              <!-- Original price (strike-through when promo or discount) -->
              <span v-if="hasPromo || hasDiscount" class="price-original"
                >{{ originalPrice }} ₪</span
              >
              <span class="currency">₪</span>{{ formattedPrice }}
              <!-- Discount badge -->
              <span v-if="hasDiscount && !hasPromo" class="discount-badge"
                >-{{ discountDisplayInfo.discountPercent }}%</span
              >
            </template>
            <template v-else>אין מחיר</template>
          </span>
        </div>

        <!-- Pikadon Column (only if item has pikadon) -->
        <div v-if="hasPikadon && pikadonValue" class="price-col">
          <span class="price-label">סה"כ פיקדון</span>
          <span class="price-value pikadon">
            <span class="currency">₪</span>{{ pikadonValue }}
          </span>
        </div>

        <!-- Total Before VAT -->
        <div v-if="lineTotal" class="price-col">
          <span class="price-label">סה"כ לפני מע"מ</span>
          <span class="price-value total-before-vat">
            <span class="currency">₪</span>{{ lineTotal }}
          </span>
        </div>

        <!-- Total With VAT -->
        <div v-if="lineTotalWithVat" class="price-col">
          <span class="price-label">סה"כ כולל מע"מ</span>
          <span class="price-value total-with-vat">
            <span class="currency">₪</span>{{ lineTotalWithVat }}
          </span>
        </div>
      </div>

      <!-- OnlyBox Message (always visible for onlyBox items, so the constraint
           is disclosed before the first add, not after) -->
      <Transition name="message">
        <div v-if="isOnlyBox" class="box-message">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>בפריט זה ניתן להזמין רק במכפלות של {{ boxSize }}</span>
        </div>
      </Transition>

      <!-- Promo Ladder (all steps) -->
      <Transition name="message">
        <div v-if="promoLadder && promoLadder.length > 0" class="promo-ladder">
          <div class="ladder-header">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              />
            </svg>
            <span>מדרגות מחיר:</span>
          </div>
          <div class="ladder-steps">
            <div
              v-for="step in promoLadder"
              :key="step.stepKey"
              class="ladder-step"
              :class="{ 'is-active': step.isActive }"
            >
              <span class="step-amount">מ-{{ step.amount }} יח':</span>
              <span class="step-price">₪{{ step.priceText }} ליח'</span>
              <span v-if="step.boxesText" class="step-boxes">({{ step.boxesText }})</span>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Next Step Hint (promo madregot) -->
      <Transition name="message">
        <div v-if="nextStepHintWithBoxes && isInCart" class="promo-hint">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            />
          </svg>
          <span>
            קנה עוד {{ nextStepHintWithBoxes.unitsNeeded }} יח'
            <span v-if="nextStepHintWithBoxes.boxesText"
              >(≈ {{ nextStepHintWithBoxes.boxesText }})</span
            >
            ותקבל מחיר של {{ nextStepHintWithBoxes.nextPrice }} ₪ ליחידה
          </span>
        </div>
      </Transition>

      <!-- Max Cap Warning (promo limit - cumulative across all items) -->
      <Transition name="message">
        <div
          v-if="maxCapInfo && (maxCapInfo.isOverMax || maxCapInfo.remaining <= 5) && isInCart"
          class="max-cap-warning"
          :class="{ 'over-max': maxCapInfo.isOverMax }"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span v-if="maxCapInfo.isOverMax"
            >יחידות מעבר ל-{{ maxCapInfo.max }} יח' יחויבו במחיר רגיל ({{
              maxCapInfo.overMaxUnits
            }}
            יח')</span
          >
          <span v-else-if="maxCapInfo.isAtMax"
            >הגעת למקסימום {{ maxCapInfo.max }} יח' במבצע (כולל פריטים נוספים במבצע)</span
          >
          <span v-else>נותרו {{ maxCapInfo.remaining }} יח' במבצע (כולל פריטים נוספים במבצע)</span>
        </div>
      </Transition>

      <!-- Actions Row - Always show stepper -->
      <div
        v-tutorial="{
          id: 'item-dialog-qty',
          text: 'מוסיפים לעגלה מכאן — הפלוס והמינוס משנים כמות, ואפשר גם להקליד כמות ישירות',
          importance: 'important',
          showOnce: true,
          autoTrigger: false,
          group: 'item-dialog-intro',
          step: 2,
          intro: false,
        }"
        class="actions-row"
      >
        <!-- Trash Button (only when in cart above one step — at exactly one
             step the stepper's minus already acts as remove, so a second
             trash button would be a confusing duplicate) -->
        <button
          v-if="isInCart && cartQty > stepSize"
          class="trash-btn"
          :disabled="!hasValidPrice"
          @click="handleTrash"
          aria-label="הסר מהעגלה"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>

        <!-- Qty Stepper -->
        <ItemDialogQtyStepper
          :qty="cartQty"
          :step="stepSize"
          :only-box="isOnlyBox"
          :box-size="boxSize"
          :disabled="!hasValidPrice"
          @update:qty="handleQtyUpdate"
          @remove="handleRemove"
        />

        <!-- Add Box Button (hidden when onlyBox=0 since stepper already uses boxSize steps) -->
        <button
          v-if="hasValidBoxSize && !isOnlyBox"
          class="add-box-btn"
          :class="{ disabled: !hasValidPrice }"
          :disabled="!hasValidPrice"
          @click="handleAddBox"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
            />
          </svg>
          <span>+ מארז</span>
        </button>
      </div>

      <!-- Price Error Modal -->
      <ItemDialogPriceErrorModal v-model:open="showPriceError" />
    </template>
  </div>
</template>

<style scoped>
.dialog-actions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
  position: relative;
}

/* Out of Stock Banner */
.out-of-stock-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: var(--border-radius-sm);
  color: #dc2626;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

.out-of-stock-banner svg {
  flex-shrink: 0;
  color: #dc2626;
}

/* Toast Notification */
.toast {
  position: absolute;
  top: -48px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  box-shadow: var(--shadow-lg);
  white-space: nowrap;
  z-index: 10;
}

.toast svg {
  flex-shrink: 0;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px) scale(0.9);
}

/* Price Display */
.price-display {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: var(--spacing-lg);
  flex-wrap: wrap;
}

.price-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
}

.price-label {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.price-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: #16a34a;
}

.price-value.no-price {
  color: var(--color-error);
  font-size: var(--font-size-sm);
}

.price-value.pikadon {
  color: #334155;
}

.price-value.total-before-vat {
  color: var(--text-primary);
  font-size: var(--font-size-lg);
}

.price-value.total-with-vat {
  color: var(--color-primary);
  font-size: var(--font-size-xl);
}

.currency {
  font-size: 0.75em;
  margin-inline-end: 2px;
}

/* Box Message */
.box-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  color: #b45309;
}

.box-message svg {
  flex-shrink: 0;
  color: #f59e0b;
}

/* Promo price styling */
.price-value.promo-price {
  color: #dc2626;
}

/* Discount price styling */
.price-value.discount-price {
  color: #059669;
}

.price-original {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  text-decoration: line-through;
  margin-inline-end: var(--spacing-xs);
}

.discount-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  margin-inline-start: var(--spacing-xs);
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  border-radius: 4px;
}

/* Promo Ladder (steps list) */
.promo-ladder {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(40, 53, 72, 0.08);
  border: 1px solid rgba(40, 53, 72, 0.25);
  border-radius: var(--border-radius-sm);
}

.ladder-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #1e293b;
}

.ladder-header svg {
  flex-shrink: 0;
  color: #283548;
}

.ladder-steps {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-inline-start: var(--spacing-lg);
}

.ladder-step {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  padding: 2px 0;
}

.ladder-step.is-active {
  color: #16a34a;
  font-weight: var(--font-weight-semibold);
  background: rgba(34, 197, 94, 0.1);
  margin: 0 calc(-1 * var(--spacing-sm));
  padding: 4px var(--spacing-sm);
  border-radius: 4px;
}

.step-amount {
  min-width: 60px;
}

.step-price {
  font-weight: var(--font-weight-medium);
}

.step-boxes {
  color: var(--text-muted);
  font-size: 11px;
}

/* Promo hint (next step) */
.promo-hint {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  color: #15803d;
}

.promo-hint svg {
  flex-shrink: 0;
  color: #22c55e;
}

/* Max cap warning */
.max-cap-warning {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  color: #b45309;
}

.max-cap-warning svg {
  flex-shrink: 0;
  color: #f59e0b;
}

.max-cap-warning.over-max {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #b91c1c;
}

.max-cap-warning.over-max svg {
  color: #ef4444;
}

.message-enter-active,
.message-leave-active {
  transition: all 0.2s ease;
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Actions Row */
.actions-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

/* Trash Button */
.trash-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.trash-btn:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: var(--color-error);
  color: var(--color-error);
}

.trash-btn:active:not(:disabled) {
  transform: scale(0.95);
  background: var(--color-error);
  color: white;
}

.trash-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Add Box Button - Same height as stepper */
.add-box-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  height: 44px;
  padding: 0 var(--spacing-md);
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.add-box-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.add-box-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.add-box-btn.disabled,
.add-box-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile & Tablet: compact, action-focused — touch targets stay 48px,
   informational blocks shrink so the stepper is reachable without scrolling */
@media (max-width: 1279px) {
  .dialog-actions {
    padding: var(--spacing-sm) var(--spacing-md);
    gap: var(--spacing-sm);
    position: sticky;
    bottom: 0;
    background: var(--bg-primary);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  }

  .toast {
    top: -44px;
    font-size: var(--font-size-xs);
    padding: var(--spacing-xs) var(--spacing-md);
  }

  /* Before anything is in the cart, the unit price is already shown in the
     details price box right above — hide the duplicate row until there are
     totals worth reporting */
  .price-display.idle {
    display: none;
  }

  .price-display {
    gap: var(--spacing-md);
  }

  .price-col {
    min-width: 0;
  }

  .price-label {
    font-size: 10px;
  }

  .price-value {
    font-size: var(--font-size-sm);
  }

  .price-value.total-before-vat {
    font-size: var(--font-size-base);
  }

  .price-value.total-with-vat {
    font-size: var(--font-size-lg);
  }

  .box-message {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: var(--font-size-xs);
    justify-content: center;
  }

  /* Promo ladder → horizontal chips: all steps visible at a glance */
  .promo-ladder {
    padding: var(--spacing-xs) var(--spacing-sm);
    gap: var(--spacing-xs);
  }

  .ladder-header {
    font-size: var(--font-size-xs);
  }

  .ladder-steps {
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    margin-inline-start: 0;
  }

  .ladder-step {
    flex: 1 1 0;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 0;
    padding: var(--spacing-xs) 2px;
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-sm);
  }

  .ladder-step.is-active {
    margin: 0;
    padding: var(--spacing-xs) 2px;
    border-color: rgba(34, 197, 94, 0.5);
  }

  .step-amount {
    min-width: 0;
    white-space: nowrap;
  }

  .step-price {
    font-weight: var(--font-weight-semibold);
  }

  .step-boxes {
    font-size: 10px;
  }

  .promo-hint,
  .max-cap-warning {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: var(--font-size-xs);
  }

  .actions-row {
    gap: var(--spacing-xs);
  }

  .trash-btn {
    width: 48px;
    height: 48px;
  }

  .add-box-btn {
    height: 48px;
    padding: 0 var(--spacing-sm);
    font-size: var(--font-size-xs);
  }
}
</style>
